import type { SortingState } from '@tanstack/react-table';
import type { DappsPageState } from '../stores/dappsPageStore';

type DappsFilters = DappsPageState['filters'];

export interface DappsPageUrlState {
  filters: DappsFilters;
  sorting: SortingState;
  page: number;
}

const defaultFilters: DappsFilters = {
  status: 'all',
  publisherType: 'all',
  hasNft: 'all',
  search: '',
};

const defaultSorting: SortingState = [{ id: 'created', desc: true }];

const statuses = new Set<DappsFilters['status']>([
  'all',
  'Draft',
  'UnderReview',
  'Published',
  'Rejected',
  'Archived',
]);

const publisherTypes = new Set<DappsFilters['publisherType']>([
  'all',
  'Organization',
  'Individual',
]);

const nftFilters = new Set<DappsFilters['hasNft']>(['all', 'true', 'false']);

const sortableColumns = new Set([
  'created',
  'updated',
  'status',
  'dappName',
  'publisherName',
]);

const parsePositiveInteger = (value: string | null) => {
  if (!value) return undefined;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 1 ? parsed : undefined;
};

const parseSorting = (value: string | null): SortingState | undefined => {
  if (!value) return undefined;
  const [id, direction = 'asc'] = value.split(':');
  if (!sortableColumns.has(id)) return undefined;
  return [{ id, desc: direction === 'desc' }];
};

export const parseDappsUql = (value: string | null): DappsPageUrlState => {
  if (!value) {
    return { filters: defaultFilters, sorting: defaultSorting, page: 1 };
  }

  const params = new URLSearchParams(value);
  const filters: DappsFilters = { ...defaultFilters };
  const status = params.get('status') as DappsFilters['status'] | null;
  const publisherType = params.get('publisherType') as
    | DappsFilters['publisherType']
    | null;
  const hasNft = params.get('hasNft') as DappsFilters['hasNft'] | null;
  const search = params.get('search');

  if (status && statuses.has(status)) filters.status = status;
  if (publisherType && publisherTypes.has(publisherType)) {
    filters.publisherType = publisherType;
  }
  if (hasNft && nftFilters.has(hasNft)) filters.hasNft = hasNft;
  if (search !== null) filters.search = search;

  return {
    filters,
    sorting: parseSorting(params.get('sort')) ?? defaultSorting,
    page: parsePositiveInteger(params.get('page')) ?? 1,
  };
};

export const buildDappsUql = ({
  filters,
  sorting,
  page,
}: DappsPageUrlState) => {
  const params = new URLSearchParams();

  if (page > 1) params.set('page', String(page));
  if (filters.status !== defaultFilters.status) {
    params.set('status', filters.status);
  }
  if (filters.publisherType !== defaultFilters.publisherType) {
    params.set('publisherType', filters.publisherType);
  }
  if (filters.hasNft !== defaultFilters.hasNft) {
    params.set('hasNft', filters.hasNft);
  }
  if (filters.search !== defaultFilters.search) {
    params.set('search', filters.search);
  }

  const sort = sorting[0];
  if (
    sort &&
    (sort.id !== defaultSorting[0]?.id || sort.desc !== defaultSorting[0]?.desc)
  ) {
    params.set('sort', `${sort.id}:${sort.desc ? 'desc' : 'asc'}`);
  }

  return params.toString();
};
